import React, { useState, useMemo, useCallback } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import { generateAccounts } from '../shared/SampleData';
import { SparkLine } from '../shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import DataTableRowActions from '@salesforce/design-system-react/components/data-table/row-actions';
import Icon from '@salesforce/design-system-react/components/icon';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import Combobox from '@salesforce/design-system-react/components/combobox';
import Badge from '@salesforce/design-system-react/components/badge';
import Pill from '@salesforce/design-system-react/components/pill';
import Modal from '@salesforce/design-system-react/components/modal';
import Toast from '@salesforce/design-system-react/components/toast';
import ToastContainer from '@salesforce/design-system-react/components/toast/container';

// ============================================================
// Constants
// ============================================================

const ITEMS_PER_PAGE = 10;

const STATUS_COLORS = {
  Active: '#2e844a',
  'At Risk': '#fe9339',
  Inactive: '#706e6b',
  Pending: '#0176d3',
  Churned: '#c23934',
};

const STATUS_BG_COLORS = {
  Active: '#e3f4e1',
  'At Risk': '#fef0e0',
  Inactive: '#f3f3f3',
  Pending: '#e1f0fd',
  Churned: '#fce4e2',
};

const STATUS_OPTIONS = [
  { id: 'Active', label: 'Active' },
  { id: 'Inactive', label: 'Inactive' },
  { id: 'Pending', label: 'Pending' },
  { id: 'At Risk', label: 'At Risk' },
];

const INDUSTRY_OPTIONS = [
  { id: 'Technology', label: 'Technology' },
  { id: 'Healthcare', label: 'Healthcare' },
  { id: 'Finance', label: 'Finance' },
  { id: 'Manufacturing', label: 'Manufacturing' },
  { id: 'Retail', label: 'Retail' },
  { id: 'Education', label: 'Education' },
  { id: 'Energy', label: 'Energy' },
];

const ROW_ACTION_OPTIONS = [
  { id: 'edit', label: 'Edit', value: 'edit' },
  { id: 'view', label: 'View Details', value: 'view' },
  { id: 'delete', label: 'Delete', value: 'delete' },
];

const ALL_COLUMNS = [
  { key: 'name', label: 'Account Name' },
  { key: 'industry', label: 'Industry' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'employees', label: 'Employees' },
  { key: 'status', label: 'Status' },
  { key: 'health', label: 'Health Score' },
  { key: 'owner', label: 'Owner' },
  { key: 'growth', label: 'Growth' },
];

// ============================================================
// Style Constants
// ============================================================

const styles = {
  card: {
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    padding: '20px',
  },
  toolbar: {
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    padding: '16px 20px',
    marginBottom: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  bulkBar: {
    backgroundColor: '#0176d3',
    color: 'white',
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '4px 4px 0 0',
  },
  pillContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '10px 20px',
    backgroundColor: '#f3f3f3',
    borderLeft: '1px solid #dddbda',
    borderRight: '1px solid #dddbda',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderTop: 'none',
    borderRadius: '0 0 4px 4px',
  },
  summaryFooter: {
    display: 'flex',
    gap: '24px',
    padding: '16px 20px',
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    marginTop: '16px',
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#706e6b',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#080707',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
    padding: '20px',
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '0 0 4px 4px',
  },
  gridCard: {
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '8px',
    padding: '20px',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  detailPanel: {
    backgroundColor: '#fafafb',
    border: '1px solid #dddbda',
    borderTop: 'none',
    padding: '20px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },
  detailField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '11px',
    color: '#706e6b',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#080707',
    fontWeight: '500',
  },
};

// ============================================================
// Custom Cell Renderers
// ============================================================

const StatusCell = ({ children, ...props }) => {
  const status = props.item.status;
  return (
    <DataTableCell {...props}>
      <Badge
        content={status}
        style={{
          backgroundColor: STATUS_BG_COLORS[status] || '#f3f3f3',
          color: STATUS_COLORS[status] || '#706e6b',
          border: 'none',
          fontWeight: '600',
          fontSize: '12px',
          padding: '2px 10px',
          borderRadius: '12px',
        }}
      />
    </DataTableCell>
  );
};
StatusCell.displayName = DataTableCell.displayName;

const HealthScoreCell = ({ children, ...props }) => {
  const health = props.item.health;
  let barColor = '#2e844a';
  if (health < 50) barColor = '#c23934';
  else if (health < 70) barColor = '#fe9339';

  return (
    <DataTableCell {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
        <div
          style={{
            flex: 1,
            height: '8px',
            backgroundColor: '#f3f3f3',
            borderRadius: '4px',
            overflow: 'hidden',
            maxWidth: '100px',
          }}
        >
          <div
            style={{
              width: `${health}%`,
              height: '100%',
              backgroundColor: barColor,
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <span style={{ fontSize: '12px', color: '#3e3e3c', fontWeight: '600', minWidth: '32px' }}>
          {health}%
        </span>
      </div>
    </DataTableCell>
  );
};
HealthScoreCell.displayName = DataTableCell.displayName;

const RevenueCell = ({ children, ...props }) => {
  const revenue = props.item.revenue;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', color: '#080707' }}>
        ${revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </span>
    </DataTableCell>
  );
};
RevenueCell.displayName = DataTableCell.displayName;

const EmployeesCell = ({ children, ...props }) => {
  const employees = props.item.employees;
  return (
    <DataTableCell {...props}>
      <span>{employees.toLocaleString('en-US')}</span>
    </DataTableCell>
  );
};
EmployeesCell.displayName = DataTableCell.displayName;

const GrowthCell = ({ children, ...props }) => {
  const growth = props.item.growth;
  const sparkData = useMemo(() => {
    const base = 50;
    const points = [];
    let current = base;
    for (let i = 0; i < 8; i++) {
      current = Math.max(10, Math.min(100, current + (growth > 0 ? Math.random() * 10 - 2 : Math.random() * -10 + 2)));
      points.push(current);
    }
    return points;
  }, [growth]);

  const sparkColor = growth >= 0 ? '#2e844a' : '#c23934';

  return (
    <DataTableCell {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <SparkLine data={sparkData} color={sparkColor} width={60} height={24} />
        <span
          style={{
            fontSize: '12px',
            fontWeight: '600',
            color: sparkColor,
          }}
        >
          {growth >= 0 ? '+' : ''}{growth}%
        </span>
      </div>
    </DataTableCell>
  );
};
GrowthCell.displayName = DataTableCell.displayName;

// ============================================================
// Column Visibility Modal
// ============================================================

const ColumnVisibilityModal = ({ isOpen, onClose, visibleColumns, onToggleColumn }) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    heading="Toggle Column Visibility"
    size="small"
    footer={[
      <Button key="done" label="Done" variant="brand" onClick={onClose} />,
    ]}
  >
    <div style={{ padding: '16px' }}>
      <p style={{ fontSize: '13px', color: '#706e6b', marginBottom: '16px' }}>
        Select which columns to show in the data table.
      </p>
      {ALL_COLUMNS.map((col) => (
        <div
          key={col.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderBottom: '1px solid #f3f3f3',
            cursor: 'pointer',
          }}
          onClick={() => onToggleColumn(col.key)}
        >
          <span style={{ fontSize: '14px', color: '#080707' }}>{col.label}</span>
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '3px',
              border: visibleColumns.includes(col.key)
                ? '2px solid #0176d3'
                : '2px solid #dddbda',
              backgroundColor: visibleColumns.includes(col.key) ? '#0176d3' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {visibleColumns.includes(col.key) && (
              <Icon category="utility" name="check" size="xx-small" style={{ fill: 'white' }} />
            )}
          </div>
        </div>
      ))}
    </div>
  </Modal>
);

// ============================================================
// Detail Modal
// ============================================================

const DetailModal = ({ isOpen, onClose, account }) => {
  if (!account) return null;
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      heading={account.name}
      size="medium"
      footer={[
        <Button key="cancel" label="Close" onClick={onClose} />,
        <Button key="edit" label="Edit Record" variant="brand" onClick={onClose} />,
      ]}
    >
      <div style={{ padding: '20px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
          }}
        >
          <DetailField label="Account Name" value={account.name} />
          <DetailField label="Industry" value={account.industry} />
          <DetailField
            label="Revenue"
            value={`$${account.revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          />
          <DetailField label="Employees" value={account.employees.toLocaleString('en-US')} />
          <DetailField label="Status" value={account.status} />
          <DetailField label="Health Score" value={`${account.health}%`} />
          <DetailField label="Owner" value={account.owner} />
          <DetailField label="Phone" value={account.phone} />
          <DetailField label="Website" value={account.website} />
          <DetailField label="Rating" value={account.rating} />
          <DetailField label="Last Activity" value={account.lastActivity} />
          <DetailField label="Growth" value={`${account.growth >= 0 ? '+' : ''}${account.growth}%`} />
        </div>
      </div>
    </Modal>
  );
};

const DetailField = ({ label, value }) => (
  <div style={styles.detailField}>
    <span style={styles.detailLabel}>{label}</span>
    <span style={styles.detailValue}>{value}</span>
  </div>
);

// ============================================================
// Card Grid View
// ============================================================

const CardGridView = ({ accounts, onSelectAccount }) => (
  <div style={styles.gridContainer}>
    {accounts.map((account) => (
      <div
        key={account.id}
        style={styles.gridCard}
        onClick={() => onSelectAccount(account)}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#0176d3';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(1,118,211,0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#dddbda';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#080707', margin: '0 0 4px 0' }}>
              {account.name}
            </h3>
            <span style={{ fontSize: '12px', color: '#706e6b' }}>{account.industry}</span>
          </div>
          <Badge
            content={account.status}
            style={{
              backgroundColor: STATUS_BG_COLORS[account.status] || '#f3f3f3',
              color: STATUS_COLORS[account.status] || '#706e6b',
              border: 'none',
              fontWeight: '600',
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '12px',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>
              Revenue
            </div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#080707' }}>
              ${account.revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>
              Employees
            </div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#080707' }}>
              {account.employees.toLocaleString('en-US')}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '600' }}>
              Health
            </span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#3e3e3c' }}>
              {account.health}%
            </span>
          </div>
          <div style={{ height: '6px', backgroundColor: '#f3f3f3', borderRadius: '3px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${account.health}%`,
                height: '100%',
                backgroundColor:
                  account.health >= 70 ? '#2e844a' : account.health >= 50 ? '#fe9339' : '#c23934',
                borderRadius: '3px',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f3f3f3', paddingTop: '10px' }}>
          <span style={{ fontSize: '12px', color: '#706e6b' }}>
            <Icon category="utility" name="user" size="xx-small" style={{ fill: '#706e6b', marginRight: '4px' }} />
            {account.owner}
          </span>
          <span
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: account.growth >= 0 ? '#2e844a' : '#c23934',
            }}
          >
            {account.growth >= 0 ? '+' : ''}{account.growth}%
          </span>
        </div>
      </div>
    ))}
  </div>
);

// ============================================================
// Expandable Detail Row
// ============================================================

const ExpandableDetailRow = ({ account }) => (
  <div style={styles.detailPanel}>
    <DetailField label="Phone" value={account.phone} />
    <DetailField label="Website" value={account.website} />
    <DetailField label="Rating" value={account.rating} />
    <DetailField label="Last Activity" value={account.lastActivity} />
    <DetailField
      label="Revenue"
      value={`$${account.revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
    />
    <DetailField label="Employees" value={account.employees.toLocaleString('en-US')} />
    <DetailField label="Growth" value={`${account.growth >= 0 ? '+' : ''}${account.growth}%`} />
    <DetailField label="Health Score" value={`${account.health}%`} />
  </div>
);

// ============================================================
// Main Component
// ============================================================

const AdvancedDataGrid = () => {
  // ---- Data ----
  const initialAccounts = useMemo(() => generateAccounts(50), []);
  const [accounts, setAccounts] = useState(initialAccounts);

  // ---- View State ----
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [currentPage, setCurrentPage] = useState(0);

  // ---- Search and Filters ----
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [industryFilter, setIndustryFilter] = useState([]);

  // ---- Sort State ----
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // ---- Selection ----
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);

  // ---- Column Visibility ----
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.map((c) => c.key)
  );
  const [columnModalOpen, setColumnModalOpen] = useState(false);

  // ---- Modals and Toasts ----
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailAccount, setDetailAccount] = useState(null);
  const [toasts, setToasts] = useState([]);

  // ---- Status filter input state for combobox ----
  const [statusInputValue, setStatusInputValue] = useState('');
  const [industryInputValue, setIndustryInputValue] = useState('');

  // ============================================================
  // Derived / Computed Data
  // ============================================================

  const filteredAccounts = useMemo(() => {
    let data = [...accounts];

    // Search
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      data = data.filter(
        (a) =>
          a.name.toLowerCase().includes(lower) ||
          a.industry.toLowerCase().includes(lower) ||
          a.owner.toLowerCase().includes(lower)
      );
    }

    // Status filter
    if (statusFilter.length > 0) {
      const statuses = statusFilter.map((s) => s.id);
      data = data.filter((a) => statuses.includes(a.status));
    }

    // Industry filter
    if (industryFilter.length > 0) {
      const industries = industryFilter.map((ind) => ind.id);
      data = data.filter((a) => industries.includes(a.industry));
    }

    // Sort
    if (sortColumn && sortDirection) {
      data.sort((a, b) => {
        let valA = a[sortColumn];
        let valB = b[sortColumn];

        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [accounts, searchTerm, statusFilter, industryFilter, sortColumn, sortDirection]);

  const totalPages = Math.ceil(filteredAccounts.length / ITEMS_PER_PAGE);
  const paginatedAccounts = useMemo(
    () =>
      filteredAccounts.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
      ),
    [filteredAccounts, currentPage]
  );

  const totalRevenue = useMemo(
    () => filteredAccounts.reduce((sum, a) => sum + a.revenue, 0),
    [filteredAccounts]
  );

  const averageHealth = useMemo(
    () =>
      filteredAccounts.length > 0
        ? Math.round(
            filteredAccounts.reduce((sum, a) => sum + a.health, 0) /
              filteredAccounts.length
          )
        : 0,
    [filteredAccounts]
  );

  const activeFilters = useMemo(() => {
    const filters = [];
    statusFilter.forEach((s) => {
      filters.push({ type: 'status', id: s.id, label: `Status: ${s.label}` });
    });
    industryFilter.forEach((ind) => {
      filters.push({ type: 'industry', id: ind.id, label: `Industry: ${ind.label}` });
    });
    if (searchTerm.trim()) {
      filters.push({ type: 'search', id: 'search', label: `Search: "${searchTerm}"` });
    }
    return filters;
  }, [statusFilter, industryFilter, searchTerm]);

  // ============================================================
  // Handlers
  // ============================================================

  const addToast = useCallback((variant, heading, content) => {
    const id = `toast-${Date.now()}`;
    setToasts((prev) => [...prev, { id, variant, heading, content }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const handleSort = useCallback(
    (sortData) => {
      const { property, sortDirection: direction } = sortData;
      setSortColumn(property);
      setSortDirection(direction === 'desc' ? 'desc' : 'asc');
      setCurrentPage(0);
    },
    []
  );

  const handleRowChange = useCallback((event, { selection }) => {
    setSelectedRows(selection);
  }, []);

  const handleRowAction = useCallback(
    (item, action) => {
      switch (action.value) {
        case 'edit':
          setDetailAccount(item);
          setDetailModalOpen(true);
          break;
        case 'view':
          setDetailAccount(item);
          setDetailModalOpen(true);
          break;
        case 'delete':
          setAccounts((prev) => prev.filter((a) => a.id !== item.id));
          addToast('success', 'Record Deleted', `${item.name} has been removed.`);
          break;
        default:
          break;
      }
    },
    [addToast]
  );

  const handleExport = useCallback(() => {
    addToast('success', 'Export Complete', `Exported ${filteredAccounts.length} records successfully.`);
  }, [filteredAccounts.length, addToast]);

  const handleExportSelected = useCallback(() => {
    addToast('success', 'Export Complete', `Exported ${selectedRows.length} selected records.`);
  }, [selectedRows.length, addToast]);

  const handleDeleteSelected = useCallback(() => {
    const selectedIds = selectedRows.map((r) => r.id);
    setAccounts((prev) => prev.filter((a) => !selectedIds.includes(a.id)));
    addToast('success', 'Records Deleted', `${selectedRows.length} records have been removed.`);
    setSelectedRows([]);
  }, [selectedRows, addToast]);

  const handleChangeStatusSelected = useCallback(() => {
    const selectedIds = selectedRows.map((r) => r.id);
    setAccounts((prev) =>
      prev.map((a) =>
        selectedIds.includes(a.id) ? { ...a, status: 'Active' } : a
      )
    );
    addToast('success', 'Status Updated', `${selectedRows.length} records set to Active.`);
    setSelectedRows([]);
  }, [selectedRows, addToast]);

  const handleRemoveFilter = useCallback(
    (filter) => {
      if (filter.type === 'status') {
        setStatusFilter((prev) => prev.filter((s) => s.id !== filter.id));
      } else if (filter.type === 'industry') {
        setIndustryFilter((prev) => prev.filter((ind) => ind.id !== filter.id));
      } else if (filter.type === 'search') {
        setSearchTerm('');
      }
      setCurrentPage(0);
    },
    []
  );

  const handleClearAllFilters = useCallback(() => {
    setStatusFilter([]);
    setIndustryFilter([]);
    setSearchTerm('');
    setStatusInputValue('');
    setIndustryInputValue('');
    setCurrentPage(0);
  }, []);

  const handleToggleColumn = useCallback((columnKey) => {
    setVisibleColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((c) => c !== columnKey)
        : [...prev, columnKey]
    );
  }, []);

  const handleRowClick = useCallback(
    (accountId) => {
      setExpandedRowId((prev) => (prev === accountId ? null : accountId));
    },
    []
  );

  const handleStatusFilterSelect = useCallback(
    (event, data) => {
      if (data.selection && data.selection.length > 0) {
        const newSelection = data.selection;
        const alreadySelected = statusFilter.find((s) => s.id === newSelection[newSelection.length - 1]?.id);
        if (alreadySelected) {
          setStatusFilter((prev) => prev.filter((s) => s.id !== alreadySelected.id));
        } else {
          setStatusFilter(newSelection);
        }
      } else {
        setStatusFilter([]);
      }
      setStatusInputValue('');
      setCurrentPage(0);
    },
    [statusFilter]
  );

  const handleIndustryFilterSelect = useCallback(
    (event, data) => {
      if (data.selection && data.selection.length > 0) {
        const newSelection = data.selection;
        const alreadySelected = industryFilter.find((ind) => ind.id === newSelection[newSelection.length - 1]?.id);
        if (alreadySelected) {
          setIndustryFilter((prev) => prev.filter((ind) => ind.id !== alreadySelected.id));
        } else {
          setIndustryFilter(newSelection);
        }
      } else {
        setIndustryFilter([]);
      }
      setIndustryInputValue('');
      setCurrentPage(0);
    },
    [industryFilter]
  );

  // ============================================================
  // Render Helpers
  // ============================================================

  const renderToolbar = () => (
    <div style={styles.toolbar}>
      {/* Search */}
      <div style={{ minWidth: '220px', flex: '0 1 260px' }}>
        <Input
          iconLeft={
            <InputIcon
              assistiveText={{ icon: 'Search' }}
              name="search"
              category="utility"
            />
          }
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(0);
          }}
        />
      </div>

      {/* Status Filter */}
      <div style={{ minWidth: '160px', flex: '0 1 180px' }}>
        <Combobox
          labels={{ placeholder: 'Filter Status' }}
          multiple
          options={STATUS_OPTIONS}
          selection={statusFilter}
          value={statusInputValue}
          events={{
            onChange: (e, { value }) => setStatusInputValue(value),
            onSelect: handleStatusFilterSelect,
            onRequestRemoveSelectedOption: (event, data) => {
              setStatusFilter(data.selection);
              setCurrentPage(0);
            },
          }}
          variant="readonly"
        />
      </div>

      {/* Industry Filter */}
      <div style={{ minWidth: '160px', flex: '0 1 180px' }}>
        <Combobox
          labels={{ placeholder: 'Filter Industry' }}
          multiple
          options={INDUSTRY_OPTIONS}
          selection={industryFilter}
          value={industryInputValue}
          events={{
            onChange: (e, { value }) => setIndustryInputValue(value),
            onSelect: handleIndustryFilterSelect,
            onRequestRemoveSelectedOption: (event, data) => {
              setIndustryFilter(data.selection);
              setCurrentPage(0);
            },
          }}
          variant="readonly"
        />
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Column Visibility */}
      <Button
        assistiveText={{ icon: 'Column Visibility' }}
        iconCategory="utility"
        iconName="matrix"
        iconSize="medium"
        variant="icon"
        title="Toggle Columns"
        onClick={() => setColumnModalOpen(true)}
      />

      {/* Export */}
      <Button
        label="Export"
        iconCategory="utility"
        iconName="download"
        iconPosition="left"
        variant="neutral"
        onClick={handleExport}
      />

      {/* View Toggle */}
      <ButtonGroup>
        <Button
          assistiveText={{ icon: 'Table View' }}
          iconCategory="utility"
          iconName="table"
          iconSize="medium"
          variant={viewMode === 'table' ? 'brand' : 'icon'}
          title="Table View"
          onClick={() => setViewMode('table')}
          style={
            viewMode === 'table'
              ? { backgroundColor: '#0176d3', borderColor: '#0176d3' }
              : {}
          }
        />
        <Button
          assistiveText={{ icon: 'Grid View' }}
          iconCategory="utility"
          iconName="kanban"
          iconSize="medium"
          variant={viewMode === 'grid' ? 'brand' : 'icon'}
          title="Card Grid View"
          onClick={() => setViewMode('grid')}
          style={
            viewMode === 'grid'
              ? { backgroundColor: '#0176d3', borderColor: '#0176d3' }
              : {}
          }
        />
      </ButtonGroup>
    </div>
  );

  const renderActiveFilters = () => {
    if (activeFilters.length === 0) return null;
    return (
      <div style={styles.pillContainer}>
        <span style={{ fontSize: '12px', color: '#706e6b', fontWeight: '600', marginRight: '4px' }}>
          Active Filters:
        </span>
        {activeFilters.map((filter) => (
          <Pill
            key={`${filter.type}-${filter.id}`}
            labels={{ label: filter.label }}
            onClick={() => handleRemoveFilter(filter)}
            onRemove={() => handleRemoveFilter(filter)}
          />
        ))}
        <Button
          label="Clear All"
          variant="base"
          onClick={handleClearAllFilters}
          style={{ fontSize: '12px', color: '#0176d3', marginLeft: '4px' }}
        />
      </div>
    );
  };

  const renderBulkActionBar = () => {
    if (selectedRows.length === 0) return null;
    return (
      <div style={styles.bulkBar}>
        <Icon category="utility" name="check" size="x-small" style={{ fill: 'white' }} />
        <span style={{ fontWeight: '700', fontSize: '14px' }}>
          {selectedRows.length} selected
        </span>
        <div style={{ flex: 1 }} />
        <Button
          label="Delete Selected"
          iconCategory="utility"
          iconName="delete"
          iconPosition="left"
          variant="destructive"
          onClick={handleDeleteSelected}
          style={{ marginRight: '8px' }}
        />
        <Button
          label="Change Status"
          iconCategory="utility"
          iconName="refresh"
          iconPosition="left"
          variant="neutral"
          onClick={handleChangeStatusSelected}
          style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
        />
        <Button
          label="Export Selected"
          iconCategory="utility"
          iconName="download"
          iconPosition="left"
          variant="neutral"
          onClick={handleExportSelected}
          style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
        />
      </div>
    );
  };

  const renderDataTable = () => (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid #dddbda',
        borderTop: selectedRows.length > 0 ? 'none' : '1px solid #dddbda',
        borderRadius: selectedRows.length > 0 ? '0' : '0',
      }}
    >
      <DataTable
        items={paginatedAccounts}
        id="advanced-data-grid-table"
        selectRows="checkbox"
        selection={selectedRows}
        onRowChange={handleRowChange}
        onSort={handleSort}
        fixedLayout
      >
        {visibleColumns.includes('name') && (
          <DataTableColumn
            key="name"
            label="Account Name"
            property="name"
            sortable
            isSorted={sortColumn === 'name'}
            sortDirection={sortDirection}
            width="180px"
          />
        )}

        {visibleColumns.includes('industry') && (
          <DataTableColumn
            key="industry"
            label="Industry"
            property="industry"
            sortable
            isSorted={sortColumn === 'industry'}
            sortDirection={sortDirection}
            width="130px"
          />
        )}

        {visibleColumns.includes('revenue') && (
          <DataTableColumn
            key="revenue"
            label="Revenue"
            property="revenue"
            sortable
            isSorted={sortColumn === 'revenue'}
            sortDirection={sortDirection}
            width="130px"
          >
            <RevenueCell />
          </DataTableColumn>
        )}

        {visibleColumns.includes('employees') && (
          <DataTableColumn
            key="employees"
            label="Employees"
            property="employees"
            sortable
            isSorted={sortColumn === 'employees'}
            sortDirection={sortDirection}
            width="110px"
          >
            <EmployeesCell />
          </DataTableColumn>
        )}

        {visibleColumns.includes('status') && (
          <DataTableColumn
            key="status"
            label="Status"
            property="status"
            sortable
            isSorted={sortColumn === 'status'}
            sortDirection={sortDirection}
            width="120px"
          >
            <StatusCell />
          </DataTableColumn>
        )}

        {visibleColumns.includes('health') && (
          <DataTableColumn
            key="health"
            label="Health Score"
            property="health"
            sortable
            isSorted={sortColumn === 'health'}
            sortDirection={sortDirection}
            width="160px"
          >
            <HealthScoreCell />
          </DataTableColumn>
        )}

        {visibleColumns.includes('owner') && (
          <DataTableColumn
            key="owner"
            label="Owner"
            property="owner"
            width="150px"
          />
        )}

        {visibleColumns.includes('growth') && (
          <DataTableColumn
            key="growth"
            label="Growth"
            property="growth"
            sortable
            isSorted={sortColumn === 'growth'}
            sortDirection={sortDirection}
            width="140px"
          >
            <GrowthCell />
          </DataTableColumn>
        )}

        <DataTableRowActions
          options={ROW_ACTION_OPTIONS}
          onAction={handleRowAction}
          dropdown={{ length: 3 }}
        />
      </DataTable>
    </div>
  );

  const renderPagination = () => {
    const start = filteredAccounts.length > 0 ? currentPage * ITEMS_PER_PAGE + 1 : 0;
    const end = Math.min((currentPage + 1) * ITEMS_PER_PAGE, filteredAccounts.length);

    return (
      <div style={styles.pagination}>
        <span style={{ fontSize: '13px', color: '#706e6b' }}>
          Showing <strong>{start}-{end}</strong> of <strong>{filteredAccounts.length}</strong> records
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Button
            label="First"
            variant="base"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(0)}
            style={{ fontSize: '13px' }}
          />
          <Button
            assistiveText={{ icon: 'Previous' }}
            iconCategory="utility"
            iconName="chevronleft"
            iconSize="small"
            variant="icon"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          />

          {/* Page Number Buttons */}
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i;
            } else if (currentPage < 3) {
              pageNum = i;
            } else if (currentPage > totalPages - 4) {
              pageNum = totalPages - 5 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: pageNum === currentPage ? '1px solid #0176d3' : '1px solid transparent',
                  borderRadius: '4px',
                  backgroundColor: pageNum === currentPage ? '#0176d3' : 'transparent',
                  color: pageNum === currentPage ? 'white' : '#3e3e3c',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: pageNum === currentPage ? '700' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {pageNum + 1}
              </button>
            );
          })}

          <Button
            assistiveText={{ icon: 'Next' }}
            iconCategory="utility"
            iconName="chevronright"
            iconSize="small"
            variant="icon"
            disabled={currentPage >= totalPages - 1}
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
          />
          <Button
            label="Last"
            variant="base"
            disabled={currentPage >= totalPages - 1}
            onClick={() => setCurrentPage(totalPages - 1)}
            style={{ fontSize: '13px' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#706e6b' }}>
            Page {currentPage + 1} of {totalPages}
          </span>
        </div>
      </div>
    );
  };

  const renderSummaryFooter = () => (
    <div style={styles.summaryFooter}>
      <div style={styles.summaryItem}>
        <Icon category="utility" name="moneybag" size="x-small" style={{ fill: '#2e844a' }} />
        <div>
          <div style={styles.summaryLabel}>Total Revenue</div>
          <div style={styles.summaryValue}>
            ${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>

      <div style={{ width: '1px', backgroundColor: '#dddbda' }} />

      <div style={styles.summaryItem}>
        <Icon category="utility" name="trending" size="x-small" style={{ fill: '#0176d3' }} />
        <div>
          <div style={styles.summaryLabel}>Avg Health Score</div>
          <div style={styles.summaryValue}>{averageHealth}%</div>
        </div>
      </div>

      <div style={{ width: '1px', backgroundColor: '#dddbda' }} />

      <div style={styles.summaryItem}>
        <Icon category="utility" name="record" size="x-small" style={{ fill: '#706e6b' }} />
        <div>
          <div style={styles.summaryLabel}>Total Records</div>
          <div style={styles.summaryValue}>{filteredAccounts.length}</div>
        </div>
      </div>

      <div style={{ width: '1px', backgroundColor: '#dddbda' }} />

      <div style={styles.summaryItem}>
        <Icon category="utility" name="groups" size="x-small" style={{ fill: '#9050e9' }} />
        <div>
          <div style={styles.summaryLabel}>Total Employees</div>
          <div style={styles.summaryValue}>
            {filteredAccounts.reduce((sum, a) => sum + a.employees, 0).toLocaleString('en-US')}
          </div>
        </div>
      </div>

      <div style={{ width: '1px', backgroundColor: '#dddbda' }} />

      <div style={styles.summaryItem}>
        <Icon category="utility" name="check" size="x-small" style={{ fill: '#2e844a' }} />
        <div>
          <div style={styles.summaryLabel}>Active Accounts</div>
          <div style={styles.summaryValue}>
            {filteredAccounts.filter((a) => a.status === 'Active').length}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCardGridView = () => (
    <CardGridView
      accounts={paginatedAccounts}
      onSelectAccount={(account) => {
        setDetailAccount(account);
        setDetailModalOpen(true);
      }}
    />
  );

  const renderExpandToggle = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 20px',
        backgroundColor: 'white',
        borderLeft: '1px solid #dddbda',
        borderRight: '1px solid #dddbda',
        borderBottom: '1px solid #f3f3f3',
        fontSize: '12px',
        color: '#706e6b',
      }}
    >
      <Icon category="utility" name="info" size="xx-small" style={{ fill: '#706e6b' }} />
      <span>Click on a row below to expand details. </span>
      {expandedRowId && (
        <Button
          label="Collapse"
          variant="base"
          onClick={() => setExpandedRowId(null)}
          style={{ fontSize: '12px', color: '#0176d3' }}
        />
      )}
      <div style={{ flex: 1 }} />
      <span style={{ fontWeight: '600', color: '#3e3e3c' }}>
        {filteredAccounts.length} record{filteredAccounts.length !== 1 ? 's' : ''} found
      </span>
    </div>
  );

  // Wrap the DataTable to intercept row clicks for expansion
  const renderInteractiveDataTable = () => (
    <div
      onClick={(e) => {
        // Find the row that was clicked
        const row = e.target.closest('tr[data-row-index]') || e.target.closest('tr');
        if (row) {
          const rowIndex = Array.from(row.parentNode?.children || []).indexOf(row);
          if (rowIndex >= 0 && rowIndex < paginatedAccounts.length) {
            // Don't expand if clicking a checkbox or action button
            const isCheckbox = e.target.closest('.slds-checkbox') || e.target.closest('input[type="checkbox"]');
            const isAction = e.target.closest('.slds-dropdown-trigger') || e.target.closest('button');
            if (!isCheckbox && !isAction) {
              handleRowClick(paginatedAccounts[rowIndex].id);
            }
          }
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      {renderDataTable()}
    </div>
  );

  // ============================================================
  // Main Render
  // ============================================================

  return (
    <ShowcasePageWrapper
      title="Advanced Data Grid"
      description="Sortable, filterable data table with inline editing and bulk actions"
      icon="table"
      iconCategory="standard"
    >
      {/* Toast Container */}
      {toasts.length > 0 && (
        <ToastContainer>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              labels={{ heading: toast.heading, details: toast.content }}
              variant={toast.variant}
              onRequestClose={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
            />
          ))}
        </ToastContainer>
      )}

      {/* Info Header */}
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #dddbda',
          borderRadius: '4px',
          padding: '16px 20px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#0176d3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon category="standard" name="account" size="small" style={{ fill: 'white' }} />
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#080707' }}>
              Accounts
            </h2>
            <p style={{ fontSize: '13px', color: '#706e6b', margin: '2px 0 0 0' }}>
              {filteredAccounts.length} total accounts
              {activeFilters.length > 0 && ` (filtered from ${accounts.length})`}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: '#e3f4e1',
              borderRadius: '16px',
            }}
          >
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#2e844a',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#2e844a' }}>
              {accounts.filter((a) => a.status === 'Active').length} Active
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: '#fef0e0',
              borderRadius: '16px',
            }}
          >
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#fe9339',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#fe9339' }}>
              {accounts.filter((a) => a.status === 'At Risk').length} At Risk
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: '#e1f0fd',
              borderRadius: '16px',
            }}
          >
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#0176d3',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#0176d3' }}>
              {accounts.filter((a) => a.status === 'Pending').length} Pending
            </span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      {renderToolbar()}

      {/* Active Filters */}
      {renderActiveFilters()}

      {/* Bulk Action Bar */}
      {renderBulkActionBar()}

      {/* View Mode: Table or Card Grid */}
      {viewMode === 'table' && (
        <>
          {renderExpandToggle()}
          {renderInteractiveDataTable()}
          {/* Expanded row detail */}
          {expandedRowId && paginatedAccounts.find((a) => a.id === expandedRowId) && (
            <ExpandableDetailRow account={paginatedAccounts.find((a) => a.id === expandedRowId)} />
          )}
        </>
      )}

      {viewMode === 'grid' && renderCardGridView()}

      {/* Pagination */}
      {renderPagination()}

      {/* Summary Footer */}
      {renderSummaryFooter()}

      {/* Column Visibility Modal */}
      <ColumnVisibilityModal
        isOpen={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
      />

      {/* Detail Modal */}
      <DetailModal
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailAccount(null);
        }}
        account={detailAccount}
      />
    </ShowcasePageWrapper>
  );
};

export default AdvancedDataGrid;
